<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import RlErrorResult from '@/components/common/RlErrorResult.vue'
import TableFilters from '@/components/common/TableFilters.vue'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import RcWorkOrderSelect from '@/components/rc/common/RcWorkOrderSelect.vue'
import { useShipments } from '@/composables/rc/shipments'
import { dateFormatter } from '@/helpers/ep/formatters'
import type { ICustomer } from '@/types/core/Customer'
import type { IShipmentFilters } from '@/types/rc/filters'
import type { IDashboardShipment } from '@/types/rc/shipments'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { QuestionFilled } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { onMounted, reactive, ref, watch } from 'vue'

interface ICarrierOption {
  key: string
  value: string
}

const props = defineProps<{
  workOrderNumber?: string
  carriersOptions: string[]
}>()

const emit = defineEmits<{
  openShipment: [shipment: IDashboardShipment]
  handleEdit: [shipment: IDashboardShipment]
}>()

const statusFilter = ref<string>()
const typeFilter = ref<string>()
const carrierFilter = ref<string>()
const carrierFilterOptions = ref<ICarrierOption[]>([])

const statusFilterOptions = [
  {
    key: 'Created',
    value: 'Created',
  },
  {
    key: 'In Transit',
    value: 'In Transit',
  },
  {
    key: 'Delivered',
    value: 'Delivered',
  },
  {
    key: 'Cancelled',
    value: 'Cancelled',
  },
  {
    key: 'Ready to Ship',
    value: 'Ready to Ship',
  },
]

const typeFilterOptions = [
  {
    key: 'Inbound',
    value: 'Inbound',
  },
  {
    key: 'Outbound',
    value: 'Outbound',
  },
]

const filterSettings = [
  {
    key: 'status',
    value: 'Status',
    options: statusFilterOptions,
    type: 'dropdown',
    placeholder: 'Status',
    modelValue: statusFilter,
    onChange: (val: string) => {
      filters.status = val
    },
    iconClass: 'label-outline-rounded',
    style: 'width: 232px',
  },
  {
    key: 'type',
    value: 'Type',
    options: typeFilterOptions,
    type: 'dropdown',
    placeholder: 'Type',
    modelValue: typeFilter,
    onChange: (val: string) => {
      filters.type = val
    },
    iconClass: 'package-2-outline',
    style: 'width: 232px',
  },
  {
    key: 'carrier',
    value: 'Carrier',
    options: carrierFilterOptions.value,
    type: 'dropdown',
    placeholder: 'Carrier',
    modelValue: carrierFilter,
    onChange: (val: string) => {
      filters.carrier = val
    },
    iconClass: 'local-shipping-outline-rounded',
    style: 'width: 232px',
  },
]

const searchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  style: 'width: 232px',
  placeholder: 'ID or tracking data',
  onChange: (val: string) => {
    if (val == '') {
      filters.fragment = undefined
    } else {
      filters.fragment = val
    }
  },
}

const filters = reactive<IShipmentFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  carrier: undefined,
  status: undefined,
  type: undefined,
  fragment: undefined,
  work_order_number: props.workOrderNumber,
})

watch([props.carriersOptions], () => {
  carrierFilterOptions.value.splice(
    0,
    carrierFilterOptions.value.length,
    ...props.carriersOptions.map((carrier: string) => ({ key: carrier, value: carrier }))
  )
})

onMounted(async () => {
  carrierFilterOptions.value.push(...props.carriersOptions.map((carrier: string) => ({ key: carrier, value: carrier })))
})

const { getShipmentsQuery } = useShipments()
const { isFetching, isError, data, error, isRefetching, refetch } = getShipmentsQuery(filters)

const handleOpenShipment = (shipment: IDashboardShipment) => {
  emit('openShipment', shipment)
}

const handleEdit = (shipment: IDashboardShipment) => {
  emit('handleEdit', shipment)
}

const { deleteShipmentMutation } = useShipments()

const hasAnyTrackingNumberOrPickDropInformation = (shipment: IDashboardShipment) => {
  return (
    shipment.tracking_information.tracking_number ||
    shipment.tracking_information.waybill_number ||
    shipment.tracking_information.bol_number ||
    shipment.customer_drop_off ||
    shipment.customer_pick_up
  )
}

const handleCustomerSelectChange = (val: ICustomer | undefined) => {
  filters.customer_id = val?.id
}

const handleWorkOrderSelectChange = (val?: { number: string }) => {
  filters.work_order_number = val?.number
}
</script>

<template>
  <div class="logistics-table-view">
    <div class="logistics-table-view__filters">
      <TableSearchFilter :search-settings="searchSettings" :disabled="isFetching" />
      <RcCustomerSelect
        v-if="!workOrderNumber"
        style="width: 232px"
        :disabled="isFetching"
        @change="handleCustomerSelectChange"
      />
      <RcWorkOrderSelect
        v-if="!workOrderNumber"
        style="width: 232px"
        :disabled="isFetching"
        @change="handleWorkOrderSelectChange"
      />
      <TableFilters :filters-list="filterSettings" />
    </div>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <el-table
        v-loading="isFetching"
        :data="data?.data"
        width="100%"
        header-cell-class-name="logistics-table-view-table-header-cell"
        row-class-name="logistics-table-view-table-row"
        @row-click="handleOpenShipment"
      >
        <el-table-column prop="id" label="#" sortable min-width="72" />
        <el-table-column v-if="!workOrderNumber" prop="work_order_number" label="Work Order #" min-width="200" />
        <el-table-column prop="type" label="Type" min-width="88" />
        <el-table-column prop="expected_containers" label="# Cont.">
          <template #default="scope">
            <span>{{ scope.row.expected_containers ?? '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column min-width="150" prop="customer" label="Customer" />
        <el-table-column label="Status" min-width="112">
          <template #default="scope">
            <StatusBadge :status="scope.row.status" />
          </template>
        </el-table-column>
        <el-table-column prop="picked_up_date" label="Picked Up Date" :formatter="dateFormatter" min-width="112" />
        <el-table-column prop="delivered_date" label="Delivered Date" :formatter="dateFormatter" min-width="112" />
        <el-table-column min-width="88" fixed="right" align="right">
          <template #default="scope">
            <el-tooltip
              v-if="hasAnyTrackingNumberOrPickDropInformation(scope.row as IDashboardShipment)"
              effect="light"
              placement="top"
            >
              <template #content>
                <p v-if="scope.row.tracking_information.tracking_number">
                  Tracking #: {{ scope.row.tracking_information.tracking_number }}
                </p>
                <p v-if="scope.row.tracking_information.waybill_number">
                  Waybill #: {{ scope.row.tracking_information.waybill_number }}
                </p>
                <p v-if="scope.row.tracking_information.bol_number">
                  Bol #: {{ scope.row.tracking_information.bol_number }}
                </p>
                <p v-if="scope.row.customer_drop_off">Dropped of by customer</p>
                <p v-if="scope.row.customer_pick_up">Picked up by customer</p>
              </template>
              <el-button link :icon="QuestionFilled" />
            </el-tooltip>
            <el-button link title="Edit" @click.stop="handleEdit(scope.row as IDashboardShipment)">
              <Icon icon="material-symbols:edit-outline" :height="16" />
            </el-button>
            <el-button
              link
              title="Delete"
              @click.stop="
                DeleteConfirmationModal(
                  'Delete Shipment',
                  'Are you sure you want to delete this shipment? This action cannot be undone',
                  scope.row.id,
                  deleteShipmentMutation.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline" :height="16" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="logistics-table-view__pagination"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.logistics-table-view {
  gap: var(--spacing-spacing-xl);
  display: flex;
  flex-direction: column;

  &__pagination {
    align-self: flex-end;
  }

  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
    flex-wrap: wrap;
  }

  &__controls {
    flex: 1 1 0%;
    display: flex;
    justify-content: flex-end;
  }
}

:deep(.logistics-table-view-table-row) {
  cursor: pointer;
}
</style>
